import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Optional, Output } from '@angular/core';
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from "@ng-bootstrap/ng-bootstrap";
import { ValidationErrorComponent } from "@shared/controls/validation-error/validation-error.component";
import { SelectOption } from "@models/ui-models/select-options";
import { ControlValueAccessor, FormControl, FormsModule, NgControl, ReactiveFormsModule } from "@angular/forms";
import { EntityType } from "@models/common/entity-type.enum";
import { DateFormatPipe } from "../../../shared/pipes/dateFormatPipe";
import { DefaulValuePipe } from "../../../shared/pipes/defaultValuePipe";
import { IndividualData } from "@models/individuals/individual-data";
import { Company } from "@models/companies/company";
import { ACNPipe } from "../../../shared/pipes/acnPipe";
import { ABNPipe } from "../../../shared/pipes/abnPipe";
import { Trust } from "@models/trusts/trust";
import { EntityIdentityApiService } from '@shared/services/entity-identity-api.service';
import { ExternalSourceType } from '@models/integrations/external-source-type.enum';
import { catchError, debounceTime, Observable, of, switchMap, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ShortEntityIdentityInfo } from '@models/integrations/short-entity-identity-info';
import { directorFullNameValidatorMsg } from '../../../shared/validators/custom-form-validators-messages';
import { FormSwitchButtonComponent } from "@shared/controls/form-switch-button/form-switch-button.component";
import { setControlDisabled } from "../../../shared/utils/set-control-disabled";
import { JurisdictionType } from "@models/trusts/trusts-enums";
import { Address, countryDefaultValue } from "../../../models/address/address";
import { IndividualVerificationStatus } from "@models/individuals/individual-verification-status";
import { NgTemplateOutlet } from "@angular/common";

class IndividualDataExtended extends IndividualData {
  individualId: string | null;
  isVerified?: boolean;

  constructor(data: Partial<IndividualDataExtended> = {}) {
    super(data);
    this.individualId = data.individualId ?? null;
    this.isVerified = data.isVerified ?? false;
  }
}

export const ExternalSourceLabels: Record<ExternalSourceType, string> = {
  [ExternalSourceType.Bolt]: 'Pravo',
  [ExternalSourceType.Xpm]: 'XPM',
  // [ExternalSourceType.APS]: 'APS',
  // [ExternalSourceType.SuperMate]: 'SuperMate',
};

export const ExternalSourceIconsPathRecord: Record<ExternalSourceType, string> = {
  [ExternalSourceType.Bolt]: 'assets/svg/pravo-logo-midnight.svg',
  [ExternalSourceType.Xpm]: 'assets/images/xero-practice-manager.svg',
  // [ExternalSourceType.APS]: 'assets/images/aps-logo.png',
  // [ExternalSourceType.SuperMate]: 'assets/images/supe-mate-logo.png',
};

export const SourceTypeOptions: SelectOption<ExternalSourceType>[] = [
  ExternalSourceType.Bolt,
  ExternalSourceType.Xpm
].map((source) => ({ label: ExternalSourceLabels[source], value: source }));

@Component({
  selector: 'app-integrations-entity-search-control',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
    ValidationErrorComponent,
    DateFormatPipe,
    DefaulValuePipe,
    ACNPipe,
    ABNPipe,
    FormSwitchButtonComponent,
    NgTemplateOutlet
  ],
  templateUrl: 'integrations-entity-search-control.component.html',
  styleUrls: ['integrations-entity-search-control.component.scss']
})
export class IntegrationsEntitySearchControlComponent implements ControlValueAccessor, OnInit {
  private entityIdentityService = inject(EntityIdentityApiService);
  private toastrService = inject(ToastrService);
  private cdr = inject(ChangeDetectorRef);

  @Input({ required: true }) entityType: EntityType = EntityType.Individual;
  @Input() allowedSources: ExternalSourceType[] = [ExternalSourceType.Bolt, ExternalSourceType.Xpm];
  @Input() placeholder = '';
  @Input() label = '';
  @Input() link = '';
  @Input() text = '';
  @Input() helperText = '';
  @Input() switchBtnText = '';
  @Input() noOptionsText = 'There are no available options.';
  @Input() suggestions: string[] = [];
  @Input() customErrors: Record<string, string> = directorFullNameValidatorMsg;
  @Input() readonly = false;

  @Output() individualSelected = new EventEmitter<IndividualData>();
  @Output() companySelected = new EventEmitter<Company>();
  @Output() trustSelected = new EventEmitter<Trust>();
  @Output() select = new EventEmitter<SelectOption>();
  @Output() switchBtnClick = new EventEmitter<boolean>();

  searchControl = new FormControl<string | null>('');
  selectedSource: ExternalSourceType = ExternalSourceType.Bolt;
  sources: SelectOption<ExternalSourceType>[] = SourceTypeOptions;
  individuals: IndividualDataExtended[] = [];
  companies: Company[] = [];
  trusts: Trust[] = [];
  selectedIndividual: IndividualDataExtended | null = null;
  selectedCompany: Company | null = null;
  selectedTrust: Trust | null = null;

  #value: string | null = null;
  dropdownOpen = false;
  loading = false;
  readonly EntityType = EntityType;
  readonly selectedSourceKey = 'selectedSource';
  readonly externalSourceIconsPathRecord: Record<string, string> = ExternalSourceIconsPathRecord;

  constructor(@Optional() protected ngControl: NgControl) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.fetchConnectedProviders();
    this.filterSuggestions();
    this.searchControl.valueChanges
      .pipe(
        tap((searchTerm) => this.value = searchTerm),
        debounceTime(500),
        switchMap((searchTerm) => this.handleSearch(searchTerm)),
        catchError((error) => {
          this.loading = false;
          this.toastrService.error('Oops something went wrong. Please try again later or fill it manually.');
          console.warn('[fetchEntities] error: ', error);
          return of([]);
        })
      )
      .subscribe();
  }

  private handleSearch(searchTerm: string | null): Observable<ShortEntityIdentityInfo[]> {
    if (!this.sources.length) return of([]);

    if (!searchTerm || searchTerm.length < 2) {
      this.individuals = [];
      this.companies = [];
      this.trusts = [];
      this.filterSuggestions();
      this.loading = false;
      return of([]);
    }

    this.loading = true;
    return this.fetchEntities();
  }

  private fetchConnectedProviders(): void {
    this.entityIdentityService.getConnectedProviders()
      .subscribe((providers) => {
        this.sources = SourceTypeOptions.filter((option) => providers.includes(option.value) && this.allowedSources.includes(option.value));

        const savedSource = localStorage.getItem(this.selectedSourceKey);
        const savedSourceEnum = ExternalSourceType[savedSource as keyof typeof ExternalSourceType];
        if (savedSource && this.sources.some((source) => source.value === savedSourceEnum)) {
          this.selectedSource = savedSourceEnum;
        }

        this.fetchEntities();
      }, (error) => {
        console.warn('[fetchConnectedProviders] error: ', error);
        this.fetchEntities();
      });
  }

  private filterSuggestions(): void {
    const filterFn = (suggestion: string) => !this.searchControl.value || suggestion.toLowerCase().includes(this.searchControl.value.toLowerCase());

    this.individuals = this.suggestions.filter(filterFn).map((suggestion) => new IndividualDataExtended({ fullName: suggestion }));
    this.companies = this.suggestions.filter(filterFn).map((suggestion) => new Company({ name: suggestion }));
    this.trusts = this.suggestions.filter(filterFn).map((suggestion) => new Trust({ name: suggestion }));
  }

  private fetchEntities(): Observable<ShortEntityIdentityInfo[]> {
    return this.entityIdentityService.listPreview(this.selectedSource, this.entityType, this.#value).pipe(
      tap((data) => {
        const filterFn = (suggestion: string) => !this.searchControl.value || suggestion.toLowerCase().includes(this.searchControl.value.toLowerCase());
        data = [
          ...this.suggestions.filter(filterFn).map((suggestion) => new ShortEntityIdentityInfo({ name: suggestion })),
          ...data
        ];

        if (this.entityType === EntityType.Individual) {
          this.individuals = data.map((item) => new IndividualDataExtended({
            firstName: item.firstName,
            middleName1: item.middleName1,
            middleName2: item.middleName2,
            lastName: item.lastName,
            fullName: item.name,
            dob: item.date ? new Date(item.date) : undefined,
            birthCity: item.birthCity,
            birthCountry: item.birthCountry,
            din: item.din,
            address: new Address({
              ...item.address,
              country: item.address?.country || countryDefaultValue
            }),
            individualId: item.id,
            isVerified: item.isVerified,
          }));
        } else if (this.entityType === EntityType.Company) {
          this.companies = data.map((item) => new Company({
            entityId: item.id,
            name: item.name,
            acn: item.entityNumber,
            registeredAddress: new Address({
              ...item.address,
              country: item.address?.country || countryDefaultValue
            }),
          }));
        } else if (this.entityType === EntityType.Trust) {
          this.trusts = data.map((item) => new Trust({
            entityId: item.id,
            name: item.name,
            abn: item.entityNumber,
            trustSubType: item.trustSubType,
            dateOfEstablishment: new Date(item.date!),
            jurisdiction: item.address?.state ? JurisdictionType[item.address?.state] : null,
            registeredAddress: new Address({
              ...item.address,
              country: item.address?.country || countryDefaultValue
            }),
          }));
        }
        this.cdr.detectChanges();
      }),
      catchError((error) => {
        this.toastrService.error('Oops something went wrong. Please try again later or fill it manually.');
        console.warn('[fetchEntities] error: ', error);
        return of([]);
      })
    );
  }

  get value(): string | null {
    return this.#value;
  }

  set value(value: string | null) {
    this.#value = value;
    this.onChange(this.#value);
  }

  get displayValidationError(): boolean {
    return !!(this.ngControl && this.ngControl.touched && this.ngControl.errors);
  }

  onChange: any = (): void => {
  };
  onTouched: any = (): void => {
  };

  writeValue(value: any): void {
    this.value = value;
    this.searchControl.setValue(value);
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
    this.searchControl.registerOnChange(fn);
  }

  onClickSwitchBtn(): void {
    this.switchBtnClick.emit(true);
  }

  setDisabledState(isDisabled: boolean): void {
    setControlDisabled(this.searchControl, isDisabled);
  }

  get entityLabel(): string {
    if (this.label) return this.label;

    switch (this.entityType) {
      case EntityType.Individual:
        return 'Individual';
      case EntityType.Company:
        return 'Company';
      case EntityType.Trust:
        return 'Trust';
    }
  }

  get selectedSourceLabel(): string {
    return ExternalSourceLabels[this.selectedSource];
  }

  selectIndividual(individual: IndividualDataExtended): void {
    this.value = individual.fullName;
    this.writeValue(this.#value);
    this.selectedIndividual = individual;
    this.individualSelected.emit(individual);
  }

  selectCompany(company: Company): void {
    this.value = company.name;
    this.selectedCompany = company;
    this.companySelected.emit(company);
  }

  selectTrust(trust: Trust): void {
    this.value = trust.name;
    this.selectedTrust = trust;
    this.trustSelected.emit(trust);
  }

  selectSource(source: ExternalSourceType): void {
    this.selectedSource = source;
    localStorage.setItem(this.selectedSourceKey, ExternalSourceType[source]);
    this.fetchEntities();
  }

  protected readonly IndividualVerificationStatus = IndividualVerificationStatus;
}

